import React, { useState, useEffect, useRef } from 'react';
import { aiService } from '../services/aiService';
import { ProgramDomain, DomainAnswers, AIFollowUpQuestion, ProgramScheduleType, ProgramIntensity } from '../types';
import { trackAIChatMessage } from '../services/analyticsService';

interface AIChatPersonalizationProps {
  domain: ProgramDomain;
  goal: string;
  duration: number;
  scheduleType: ProgramScheduleType;
  customDays?: number[];
  questsPerDay: number;
  intensity: ProgramIntensity;
  domainAnswers: DomainAnswers;
  preferredTimeStart?: string;
  preferredTimeEnd?: string;
  availableDays?: number[];
  onComplete: (answers: { [key: string]: string }) => void;
  onSkip: () => void;
}

interface ChatMessage {
  role: 'ai' | 'user';
  content: string;
  timestamp: number;
}

const AIChatPersonalization: React.FC<AIChatPersonalizationProps> = ({
  domain,
  goal,
  duration,
  scheduleType,
  customDays,
  questsPerDay,
  intensity,
  domainAnswers,
  preferredTimeStart,
  preferredTimeEnd,
  availableDays,
  onComplete,
  onSkip
}) => {
  const [questions, setQuestions] = useState<AIFollowUpQuestion[]>([]);
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load all questions upfront
  useEffect(() => {
    const loadQuestions = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Build context string from schedule and time availability info
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        let scheduleContext = '';

        if (scheduleType === 'daily') {
          scheduleContext = `They will work on this EVERY DAY with ${questsPerDay} tasks per day (${intensity} intensity).`;
        } else if (scheduleType === 'every_other_day') {
          scheduleContext = `They will work on this EVERY OTHER DAY with ${questsPerDay} tasks per day (${intensity} intensity).`;
        } else if (scheduleType === 'custom' && customDays && customDays.length > 0) {
          const daysList = customDays.map(d => dayNames[d]).join(', ');
          scheduleContext = `They will work on this ${customDays.length} days per week (${daysList}) with ${questsPerDay} tasks per day (${intensity} intensity).`;
        }

        // Add time availability context
        let timeContext = '';
        if (preferredTimeStart && preferredTimeEnd) {
          timeContext = `They prefer to work between ${preferredTimeStart} and ${preferredTimeEnd}.`;
        }
        if (availableDays && availableDays.length > 0) {
          const availableDaysList = availableDays.map(d => dayNames[d]).join(', ');
          timeContext += ` They are available on: ${availableDaysList}.`;
        }

        const fullContext = scheduleContext + (timeContext ? ' ' + timeContext : '');

        const generatedQuestions = await aiService.generateFollowUpQuestions(
          domain,
          goal,
          duration,
          {
            ...domainAnswers,
            _scheduleContext: fullContext
          } as DomainAnswers
        );

        if (generatedQuestions.length === 0) {
          // No questions generated - skip to completion
          onSkip();
          return;
        }

        setQuestions(generatedQuestions);
        setIsLoading(false);
      } catch (err: any) {
        console.error('Error loading AI questions:', err);
        setError('Failed to load personalization questions. You can skip this step.');
        setIsLoading(false);
      }
    };

    loadQuestions();
  }, [domain, goal, duration, scheduleType, customDays, questsPerDay, intensity, domainAnswers]);

  const handleSubmitAllAnswers = () => {
    // Check if at least one question is answered
    const hasAnswers = Object.keys(answers).length > 0;
    
    if (!hasAnswers || isSubmitting) return;

    setIsSubmitting(true);
    
    // Complete immediately with all answers
    setTimeout(() => {
      onComplete(answers);
    }, 300);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] relative">
        <div className="flex flex-col items-center justify-center space-y-6">
          {/* Spinning loader */}
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500/30 blur-lg rounded-full"></div>
            <div className="relative animate-spin rounded-full h-16 w-16 border-4 border-transparent border-t-blue-500 border-r-cyan-500"></div>
          </div>

          {/* Loading text */}
          <div className="text-center">
            <p className="text-blue-400 font-orbitron font-bold text-sm uppercase tracking-wider mb-2 flex items-center justify-center gap-2">
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
              System Analyzing
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
            </p>
            <p className="text-gray-400 font-rajdhani text-sm">
              Processing your goal data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 relative">
        <div className="relative">
          {/* Error icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-slate-700/50 backdrop-blur-sm rounded-full flex items-center justify-center border border-slate-500/50">
              <span className="text-3xl">⚠️</span>
            </div>
          </div>
          
          {/* Error message */}
          <div className="bg-slate-700/50 backdrop-blur-sm border border-red-500/30 rounded-xl p-4 mb-4">
            <p className="text-red-400 text-sm font-rajdhani text-center">{error}</p>
          </div>
          
          {/* Skip button */}
          <button
            onClick={onSkip}
            className="w-full px-6 py-3 bg-slate-700/50 hover:bg-slate-600/70 border border-slate-600/50 hover:border-slate-500/60 text-white rounded-xl transition-all font-orbitron font-bold text-xs uppercase tracking-wider"
          >
            Skip This Step
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-blue-700/60 border-b-blue-700/60 rounded-3xl overflow-hidden"
      style={{
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
      }}
    >
        {/* Header */}
        <div className="border-b border-blue-500/20 p-6 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm"
          style={{
            boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div className="flex items-center gap-3">
            {/* System Icon */}
            <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse"></div>
            <span className="text-xs sm:text-sm font-orbitron text-system-blue tracking-wider uppercase">
              ［ System ］
            </span>
            
            <div className="flex-1">
              <h3 className="font-orbitron font-bold text-white tracking-wider uppercase text-sm">
                System Personalization
              </h3>
              <p className="text-xs text-gray-400 font-rajdhani mt-0.5">
                {questions.length} Questions
              </p>
            </div>
            
            {/* Skip button */}
            <button
              onClick={onSkip}
              className="text-xs leading-none transition-all bg-gradient-to-b from-slate-700/50 to-slate-800/50 rounded-full px-3 py-1 border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60 text-gray-400 hover:text-white"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              Skip
            </button>
          </div>
        </div>

        {/* Questions Form */}
        <div className="bg-slate-800/30 backdrop-blur-sm p-6 space-y-4 relative max-h-[500px] overflow-y-auto">
          <div className="space-y-4">
            {/* Intro Message */}
            <div className="bg-gradient-to-b from-blue-950/40 via-slate-950/30 to-blue-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-blue-700/60 border-b-blue-700/60 rounded-3xl p-6"
              style={{
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚡</span>
                <div>
                  <p className="text-xs font-orbitron uppercase tracking-wider mb-1 text-blue-400">
                    System
                  </p>
                  <p className="text-sm leading-relaxed font-rajdhani text-gray-300">
                    Please answer these questions to tailor the program just for you. You can skip any question if you prefer.
                  </p>
                </div>
              </div>
            </div>

            {/* All Questions */}
            {questions.map((question, index) => (
              <div
                key={question.id}
                className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-4 hover:border-blue-500/40 transition-all"
                style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="flex items-start gap-2 mb-3">
                  <span className="text-blue-400 font-orbitron text-sm font-bold">Q{index + 1}:</span>
                  <label className="text-sm font-rajdhani text-white flex-1">
                    {question.question}
                  </label>
                </div>

                {question.type === 'select' && question.options ? (
                  <select
                    value={answers[question.id] || ''}
                    onChange={(e) => setAnswers(prev => ({...prev, [question.id]: e.target.value}))}
                    className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all font-rajdhani"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <option value="" disabled>Select an option...</option>
                    {question.options.map((option, optIndex) => (
                      <option key={optIndex} value={option} className="bg-slate-800 text-white">
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <textarea
                    value={answers[question.id] || ''}
                    onChange={(e) => setAnswers(prev => ({...prev, [question.id]: e.target.value}))}
                    placeholder="Your answer..."
                    rows={question.type === 'select' ? 1 : 2}
                    className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all font-rajdhani resize-none"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="border-t border-slate-600/30 p-6 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm"
          style={{
            boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05)'
          }}
        >
          <button
            onClick={handleSubmitAllAnswers}
            disabled={Object.keys(answers).length === 0 || isSubmitting}
            className={`relative w-full px-6 py-3 rounded-2xl font-orbitron font-bold text-sm uppercase tracking-wider transition-all ${
              Object.keys(answers).length > 0 && !isSubmitting
                ? 'bg-gradient-to-b from-blue-800/60 via-purple-800/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 hover:scale-105 text-white overflow-hidden'
                : 'bg-slate-700/50 text-gray-600 cursor-not-allowed border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60'
            }`}
            style={Object.keys(answers).length > 0 && !isSubmitting ? {
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
            } : {
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
            }}
          >
            {isSubmitting ? (
              <span>Creating Program...</span>
            ) : (
              <>
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                <span className="relative z-10">🚀 Generate My Program</span>
              </>
            )}
          </button>
          
          <p className="text-center text-xs text-gray-500 font-rajdhani mt-2">
            {Object.keys(answers).length > 0 
              ? `${Object.keys(answers).length} of ${questions.length} questions answered`
              : 'Answer at least one question to continue'
            }
          </p>
        </div>
      </div>
  );
};

export default AIChatPersonalization;

